import PMBSelect from 'components/PMBSelect'
import { STATUS_TYPE } from 'constants'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Info } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, FormGroup, Input, Label, Modal, ModalBody } from 'reactstrap'
import { BUSINESS_WHISTLEBLOWING } from 'utils/subjectActions'
import {
  useFetchUserTimsBlowingsQuery,
  useUpdateWhistleBlowingStatusMutation,
} from './whistleBlowingsListApiSlice'
import { setIsOpenModalAssign } from './whistleBlowingsListSlice'

const ModalAssign = ({ open, row, toggle, statusType, onSuccess }) => {
  const idWhistleBlowing = row?.id

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const { tag } = useSelector((s) => s.whistleBlowingsList)
  const { abilityRole } = useSelector((s) => s.auth)
  const { profile } = useSelector((s) => s.dashboard)

  const isHaveRoleWhistleBlowing = abilityRole?.some(
    (role) => role?.subject === BUSINESS_WHISTLEBLOWING,
  )

  const [updateWhistleBlowingStatus, { isLoading: isLoadingStatusMutation }] =
    useUpdateWhistleBlowingStatusMutation()

  let [page] = useState({ page: tag.page ?? 0 })

  const { data: dataUsers } = useFetchUserTimsBlowingsQuery({
    page: page?.page,
    size: 999,
  })

  const usersTimOptions =
    dataUsers?.items?.length > 0
      ? dataUsers?.items
          ?.filter((user) => {
            if (isHaveRoleWhistleBlowing) {
              return true
            }
            return (
              user?.userId !== profile?.userId &&
              user?.userId !== row?.assignedUserId
            )
          })
          .map((user) => ({
            value: user.userId,
            label: user.name,
          }))
      : []

  const inputRef = useRef(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [notes, setNotes] = useState('')

  const resetState = () => {
    setSelectedUser(null)
    setNotes('')
  }

  // HANDLE SUBMIT
  const handleSubmit = async () => {
    try {
      let assignedUserId = null

      if (statusType !== STATUS_TYPE.RESOLVED) {
        assignedUserId = selectedUser?.value
      }

      const res = await updateWhistleBlowingStatus({
        id: idWhistleBlowing,
        statusType,
        notes,
        assignedUserId,
      }).unwrap()

      if (!res.error) {
        dispatch(setIsOpenModalAssign())
        resetState()

        enqueueSnackbar(`Berhasil simpan!`, { variant: 'success' })

        onSuccess()

        navigate('/whistle-blowings')
      } else {
        enqueueSnackbar(`Gagal simpan!`, { variant: 'error' })
      }
    } catch (error) {
      console.warn(error)
      enqueueSnackbar(`Gagal simpan!`, { variant: 'error' })
    }
  }

  // HANDLE EDIT
  useMemo(() => {
    if (row?.assignedUserId) {
      const userDataMapping = {
        value: row?.assignedUserId,
        label: row?.assigned?.name,
      }
      setSelectedUser(userDataMapping)
    }
  }, [row])

  // HANDLE AUTO FOCUS IN TEXT AREA
  useEffect(() => {
    if (statusType !== STATUS_TYPE.ASSIGNED && open) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 100)
    }
  }, [statusType, open])

  return (
    <Modal
      className="p-4"
      centered
      isOpen={open}
      toggle={() => {
        toggle()
      }}
      autoFocus={false}
      data-testid="whistle-modal-assign"
    >
      <ModalBody>
        {statusType !== STATUS_TYPE.RESOLVED && (
          <h6>Pilih user yang akan menindaklanjuti</h6>
        )}

        {statusType !== STATUS_TYPE.RESOLVED && (
          <FormGroup>
            <Label className="text-sm">Users</Label>
            <PMBSelect
              options={usersTimOptions}
              value={selectedUser}
              onChange={(selectedOptions) => setSelectedUser(selectedOptions)}
              placeholder="Pilih users"
              testId="whistle-form-user"
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label className="text-sm">Note</Label>

          <div
            style={{
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
            }}
          >
            <Input
              innerRef={inputRef}
              style={{
                border: 'none',
                minHeight: 200,
                borderRadius: '4px',
                padding: '8px',
                boxSizing: 'border-box',
              }}
              value={notes}
              className="border-input"
              type="textarea"
              placeholder="Masukkan catatan..."
              data-testid="whistle-form-note"
              onChange={(e) => {
                const value = e.target.value
                if (value.trim() !== '') {
                  setNotes(value)
                } else {
                  setNotes('')
                }
              }}
            />
          </div>
        </FormGroup>

        {statusType === STATUS_TYPE.RESOLVED && (
          <div className="mb-2 d-flex gap-2">
            <Info className="text-primary" size={30} />
            <span className="text-xs">
              Setelah disimpan, Anda tidak dapat melakukan assignment ulang.
              Status ini menandakan bahwa masalah telah terselesaikan
              sepenuhnya.
            </span>
          </div>
        )}

        <div className="d-flex gap-3">
          <Button
            onClick={() => {
              toggle()
              resetState()
            }}
            color="primary"
            outline
            block
            data-testid="whistle-form-cancel-button"
          >
            Batal
          </Button>
          <Button
            disabled={
              isLoadingStatusMutation ||
              (statusType === STATUS_TYPE.RESOLVED && !notes) ||
              (statusType !== STATUS_TYPE.RESOLVED && (!selectedUser || !notes))
            }
            color="primary"
            block
            onClick={() => {
              handleSubmit()
            }}
            data-testid="whistle-form-submit-button"
          >
            Simpan
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ModalAssign
